import React, { FC, ReactNode } from "react";
import classNames, { Argument } from "classnames/dedupe";

import { TThemeVariant, useTheme } from "../../../Utility/ThemeProvider";
import {
  ShoppingBagItem,
  IShoppingBagItem,
  TShoppingBagItemType,
} from "../ShoppingBagItem";
import { SvgIcon } from "../../../Icons";
import Alert from "../../../DataDisplay/Alert/Alert";
import { Divider } from "../../../DataDisplay/Divider";

export interface IShoppingBag {
  classes?: {
    root?: Argument;
  };
  /** Shopping bag items data */
  items: IShoppingBagItem[];
  /** Type of visual representation. Default, small or "Shop the look". */
  type?: TShoppingBagItemType;
  /** Theme */
  variant?: TThemeVariant;
  labels?: {
    /** Text content (i18n) for "not available item" alert */
    unavailable: ReactNode;
  };
  readonly?: boolean;
  onDeleteItem?(id: string): void;
  onFavouriteItem?(id: string): void;
  onItemQtyChange?(id: string, newQuantity: string): void;
  hasDisableQuantity?: boolean;
}

const ShoppingBag: FC<IShoppingBag> = ({
  classes,
  items,
  type = "default",
  variant = "light",
  readonly,
  labels = {
    unavailable: "Item is currently unavailable",
  },
  onDeleteItem,
  onFavouriteItem,
  onItemQtyChange,
  hasDisableQuantity,
}) => {
  const theme: TThemeVariant = useTheme(variant);

  const rootClasses = classNames(
    "position-relative px-6",
    {
      "bg-color-blue": theme === "dark",
    },
    classes?.root,
  );

  return (
    <div className={rootClasses} data-testid="ShoppingBag">
      <ul className="shopping-bag--product-cards">
        {items?.map((data, index) => (
          <li key={data.id}>
            <ShoppingBagItem
              {...data}
              type={type}
              hasDisableQuantity={hasDisableQuantity}
              readonly={readonly}
              onQtyChange={(newQuantity) =>
                onItemQtyChange?.(data.id, newQuantity)
              }
              onFavourite={() => onFavouriteItem?.(data.id)}
              onRemove={() => onDeleteItem?.(data.id)}
            />
            {items.length > index + 1 && type === "look" ? (
              <>
                {data.disabled ? (
                  <div
                    className="mb-4 cursor-disabled"
                    data-testid="ShoppingBag__disabled-alert"
                  >
                    <Alert
                      headerContent={
                        <>
                          <SvgIcon icon="SoldOutIcon" />
                          <span>{labels.unavailable}</span>
                        </>
                      }
                      headerVariant="simple"
                      isWarningSimple
                    />
                  </div>
                ) : null}
                <Divider color={theme === "dark" ? "blue" : "white"} />
              </>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  );
};

export default ShoppingBag;
